import { CircleDot } from "lucide-react";

import type { Encounter } from "@elation/api-client/src/stitch-web";
import { isPausedStep, isRecordingActiveStep } from "@elation/transcribe";

import {
  useTourGuideStep,
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_ID,
  VISIT_TOUR_STEP_IDS,
  VisitTour,
} from "@src/features/tourguide";
import { HorizontalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";

import type { PatientSelectorPatientSummaryV2 } from "../PatientSelector";
import { VisitRecordingAudioLevelMeter } from "../VisitRecordingAudioLevelMeter";
import { useAudioLevels } from "../VisitRecordingAudioLevelMeter/useAudioLevels";
import type { VisitRecordingV2 } from "../VisitRecordingBanner";
import { VisitScribeSettings } from "../VisitScribeSettings";

export interface VisitStartControlsProps {
  encounter?: Encounter | null;
  encounterId: number;
  patient?: PatientSelectorPatientSummaryV2 | null;
  recording?: VisitRecordingV2;
  onStartVisit?: () => void;
}

export interface VisitStartControlsViewProps {
  showScribeSettings?: boolean;
  showAudioLevelMeter?: boolean;
  isAudioLevelActive?: boolean;
  audioLevels?: number[];
  showStartVisit?: boolean;
  isStartDisabled?: boolean;
  onStartVisit?: () => void;
}

const TOUR_AUDIO_LEVELS = [0.18, 0.34, 0.62, 0.78, 0.44, 0.28, 0.55, 0.36];

function VisitStartControlsDemo({
  encounter,
  encounterId,
  isActive,
  startVisit,
}: {
  encounter: Encounter;
  encounterId: number;
  isActive: boolean;
  startVisit: (payload?: Record<string, unknown>) => Promise<unknown>;
}) {
  const isRecording = encounter.status === "in_progress";

  return (
    <VisitTour.StartVisitStep>
      <VisitStartControlsView
        showScribeSettings={false}
        showAudioLevelMeter={isRecording}
        isAudioLevelActive={isRecording}
        audioLevels={TOUR_AUDIO_LEVELS}
        showStartVisit={!isRecording}
        isStartDisabled={!isActive}
        onStartVisit={() => void startVisit({ encounterId })}
      />
    </VisitTour.StartVisitStep>
  );
}

function VisitStartControlsLive({
  recording,
  onStartVisit,
}: {
  recording?: VisitRecordingV2;
  onStartVisit?: () => void;
}) {
  const audioLevels = useAudioLevels({
    audioContext: recording?.audioContext,
    audioSource: recording?.audioSource,
    isActive: recording ? isRecordingActiveStep(recording.step) : false,
  });

  if (!recording) {
    return (
      <VisitTour.StartVisitStep>
        <VisitStartControlsView
          showScribeSettings
          showStartVisit
          isStartDisabled={false}
          onStartVisit={onStartVisit}
        />
      </VisitTour.StartVisitStep>
    );
  }

  const isInitial = recording.step === "initial";
  const isRecording =
    isRecordingActiveStep(recording.step) || isPausedStep(recording.step);
  const canStartRecording =
    isInitial && recording.isReady && recording.isSupported !== false;
  const showScribeSettings = recording.isSupported !== false;

  return (
    <VisitTour.StartVisitStep>
      <VisitStartControlsView
        showScribeSettings={showScribeSettings}
        showAudioLevelMeter={isRecording}
        isAudioLevelActive={isRecordingActiveStep(recording.step)}
        audioLevels={audioLevels}
        showStartVisit={isInitial}
        isStartDisabled={!canStartRecording}
        onStartVisit={recording.onStart}
      />
    </VisitTour.StartVisitStep>
  );
}

export function VisitStartControlsView({
  showScribeSettings = false,
  showAudioLevelMeter = false,
  isAudioLevelActive = false,
  audioLevels,
  showStartVisit = false,
  isStartDisabled = false,
  onStartVisit,
}: VisitStartControlsViewProps) {
  return (
    <HorizontalFlexbox alignItems="center" className="shrink-0 gap-2">
      {showAudioLevelMeter ? (
        <VisitRecordingAudioLevelMeter
          isActive={isAudioLevelActive}
          levels={audioLevels}
        />
      ) : null}
      {showStartVisit ? (
        <Button
          type="button"
          className="h-8 rounded-[9px] bg-destructive px-4 text-[12.5px] font-semibold shadow-none hover:bg-destructive/90"
          disabled={isStartDisabled}
          onClick={onStartVisit}
        >
          <CircleDot className="size-3.5" aria-hidden="true" />
          Start visit
        </Button>
      ) : null}
      {showScribeSettings ? <VisitScribeSettings /> : null}
    </HorizontalFlexbox>
  );
}

export function VisitStartControls({
  encounter,
  encounterId,
  patient,
  recording,
  onStartVisit,
}: VisitStartControlsProps) {
  const startVisitTourStep = useTourGuideStep(
    VISIT_TOUR_ID,
    VISIT_TOUR_STEP_IDS.startVisit,
  );
  const isVisitTourEncounter = encounterId === VISIT_TOUR_ENCOUNTER_ID;

  if (!patient) {
    return null;
  }

  if (isVisitTourEncounter && encounter) {
    return (
      <VisitStartControlsDemo
        encounter={encounter}
        encounterId={encounterId}
        isActive={startVisitTourStep.isActive}
        startVisit={startVisitTourStep.actions.startVisit}
      />
    );
  }

  return (
    <VisitStartControlsLive recording={recording} onStartVisit={onStartVisit} />
  );
}
